<template>
  <form @submit.prevent="handleSubmit" id="vendor_create_form">
    <!-- Vendor Name -->
    <div class="mb-4 mt-2">
      <label class="form-label form-label-thin">Vendor Name <span class="text-danger">*</span></label>
      <input type="text" class="form-control form-control-thin" id="name" name="name" ref="name" v-model="form.name"
        placeholder="Enter vendor name" />
    </div>

    <!-- Description -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Description</label>
      <textarea class="form-control form-control-thin" id="description" name="description" ref="description"
        v-model="form.description" placeholder="Vendor description" rows="2"></textarea>
    </div>

    <div class="mb-4">
      <FileUploader v-model:files="form.files"
        :help-text="'Upload vendor-related documents or images. Max 10 files.'" />
    </div>

    <!-- Contacts Section -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Contacts</label>
      <div class="text-primary mb-2 small" style="cursor: pointer; text-decoration: none;" @click="addContact">
        <i class="fa fa-plus"></i> Add Contact
      </div>

      <div v-for="(contact, index) in form.contacts" :key="index" class="contact-card mb-4 p-3 border rounded"
        :class="{ 'contact-card-highlight': index === form.contacts.length - 1 }">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong class="text-primary">Contact {{ index + 1 }}</strong>
          <div class="text-danger" style="cursor: pointer;" @click="removeContact(index)"
            v-if="form.contacts.length > 1">
            <i class="fa fa-times"></i> Remove
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-2">
            <input type="text" class="form-control form-control-thin" :name="`contacts[${index}][name]`"
              v-model="contact.name" placeholder="Contact Name *" />
            <div v-if="errors['contacts.' + index + '.name']" class="text-danger small">
              {{ errors['contacts.' + index + '.name'][0] }}
            </div>
          </div>

          <div class="col-md-6 mb-2">
            <input type="email" class="form-control form-control-thin" :name="`contacts[${index}][email]`"
              v-model="contact.email" placeholder="Email" />
            <div v-if="errors['contacts.' + index + '.email']" class="text-danger small">
              {{ errors['contacts.' + index + '.email'][0] }}
            </div>
          </div>

          <div class="col-md-6 mb-2">
            <input type="text" class="form-control form-control-thin" :name="`contacts[${index}][phone]`"
              v-model="contact.phone" placeholder="Phone" />
          </div>

          <div class="col-md-6 mb-2">
            <input type="text" class="form-control form-control-thin" :name="`contacts[${index}][role]`"
              v-model="contact.role" placeholder="Role (e.g., Manager, Coordinator)" />
          </div>
        </div>
      </div>
      <div v-if="errors.contacts" class="text-danger small">{{ errors.contacts }}</div>
    </div>



    <div class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label form-label-thin mb-0">
          Associated Assets
        </label>

        <button type="button" class="btn btn-sm btn-link text-primary text-decoration-none p-0"
          @click="openCreateAssetModal">
          <i class="fa fa-plus-circle me-1"></i>
          Add Asset
        </button>
      </div>

      <MultiSelect v-model="form.asset_ids" :multiple="true" :options="assets" option-value="id" option-label="name"
        placeholder="Select Assets..." name="asset_ids" id="asset_ids" ref="asset_ids" />
    </div>

    <FormModal ref="createAssetModal" title="Create New Asset" id="create_asset_form_modal">
      <CreateAsset ref="createAssetForm" @created="onAssetCreated" @close="closeCreateAssetModal" />
    </FormModal>
    <div class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label form-label-thin mb-0">
          Associated Locations
        </label>

        <button type="button" class="btn btn-sm btn-link text-primary text-decoration-none p-0"
          @click="openCreateLocationModal">
          <i class="fa fa-plus-circle me-1"></i>
          Add Location
        </button>
      </div>

      <MultiSelect v-model="form.location_ids" :options="locations" option-value="id" option-label="name"
        name="location_ids" id="location_ids" ref="location_ids" placeholder="Select locations..." multiple />
    </div>

    <!-- Create Location Modal -->
    <FormModal ref="createLocationModal" title="Create New Location" id="create_location_form_modal">
      <CreateLocation ref="createLocationForm" :Parentlocations="Parentlocations" @created="onLocationCreated"
        @close="closeCreateLocationModal" />
    </FormModal>
    <!-- Vendor Type -->
    <div class="mb-4">
      <label class="form-label form-label-thin">Vendor Type</label>
      <MultiSelect v-model="form.vendor_type" :options="vendorTypeOptions" option-value="value" option-label="label"
        name="vendor_type" id="vendor_type" ref="vendor_type" placeholder="Select vendor type..." :multiple="false"
        @update:model-value="onVendorTypeChange" />
    </div>

    <div v-if="form.vendor_type === 'contractor'" class="mb-4">
      <label class="form-label form-label-thin">Nature of Contractor</label>
      <MultiSelect v-model="form.contractor_nature" :options="contractorNatureOptions" option-value="value"
        option-label="label" name="contractor_nature" id="contractor_nature" ref="contractor_nature"
        placeholder="Select contractor nature..." :multiple="true" />

      <div
        v-if="Array.isArray(form.contractor_nature) ? form.contractor_nature.includes('other') : form.contractor_nature === 'other'"
        class="mt-2">
        <input type="text" class="form-control form-control-thin" v-model="form.contractor_nature_other"
          placeholder="Specify other nature" />
      </div>

      <div class="mb-4 mt-3">
        <div class="d-flex align-items-center justify-content-between">
          <label class="form-label form-label-thin mb-0">Contractor Risk Rating</label>
          <button type="button" class="btn btn-link p-0 small text-decoration-none" @click="showRatingGuide = true">
            <i class="fa fa-info-circle"></i> Rating Guide
          </button>
        </div>
        <MultiSelect v-model="form.contractor_risk_rating" :options="riskRatingOptions" option-value="value"
          option-label="label" name="contractor_risk_rating" id="contractor_risk_rating" ref="contractor_risk_rating"
          placeholder="Select contractor rating..." :multiple="false" class=" mt-2" />
        <div v-if="errors.contractor_risk_rating" class="text-danger small mt-1">
          {{ errors.contractor_risk_rating[0] }}
        </div>
      </div>

      <div v-if="showRatingGuide" class="rating-guide-modal-backdrop">
        <div class="rating-guide-modal">
          <div class="rating-guide-header">
            <h5 class="mb-1">Risk Level</h5>
            <button type="button" class="btn-close" @click="showRatingGuide = false"></button>
          </div>
          <div class="rating-guide-body">
            <table class="table table-bordered mb-0 rating-guide-table">
              <thead>
                <tr>
                  <th scope="col">Risk Level</th>
                  <th scope="col">Examples</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span class="risk-level-badge risk-low">Low</span></td>
                  <td>Office equipment maintenance, landscaping, pest device servicing</td>
                </tr>
                <tr>
                  <td><span class="risk-level-badge risk-medium">Medium</span></td>
                  <td>HVAC maintenance, utility maintenance, forklift servicing</td>
                </tr>
                <tr>
                  <td><span class="risk-level-badge risk-high">High</span></td>
                  <td>Welding, fabrication, electrical work, roof work, refrigeration systems, production equipment
                    repairs</td>
                </tr>
                <tr>
                  <td><span class="risk-level-badge risk-critical">Critical</span></td>
                  <td>Work over exposed product, food-contact equipment modifications, major equipment installations
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="rating-guide-footer text-end">
            <button type="button" class="btn btn-sm btn-primary" @click="showRatingGuide = false">Close</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="form.vendor_type === 'vendor'" class="mb-4">
      <label class="form-label form-label-thin">Nature of Vendor</label>
      <MultiSelect v-model="form.vendor_nature" :options="vendorNatureOptions" option-value="value" option-label="label"
        name="vendor_nature" id="vendor_nature" ref="vendor_nature" placeholder="Select nature of vendor..."
        :multiple="true" @update:model-value="onVendorNatureChange" />

      <div v-if="form.vendor_nature.includes('other')" class="mt-2">
        <input type="text" class="form-control form-control-thin" v-model="form.vendor_nature_other"
          placeholder="Specify other nature" />
      </div>

      <div v-if="form.vendor_nature.length && !form.vendor_nature.includes('other')" class="mt-4">
        <label class="form-label form-label-thin">Associated {{ associatedLabel }}</label>
        <MultiSelect v-model="form.material_ids" :groups="materialGroups" option-value="value" option-label="label"
          name="material_ids" id="material_ids" ref="material_ids" :multiple="true"
          :placeholder="`Select ${associatedLabel}...`" />
      </div>

      <div class="mb-4 mt-3">
        <label class="form-label form-label-thin">Vendor Risk Rating</label>
        <MultiSelect v-model="form.vendor_risk_rating" :options="riskRatingOptions" option-value="value"
          option-label="label" name="vendor_risk_rating" id="vendor_risk_rating" ref="vendor_risk_rating"
          placeholder="Select vendor rating..." :multiple="false" class=" mt-2" />
      </div>

      <div class="mb-4">
        <label class="form-label form-label-thin">Any Input Qualification Requirements</label>
        <div class="d-flex gap-3 align-items-center">
          <label class="radio-inline mb-0">
            <input type="radio" name="qualification_status" value="yes" v-model="form.qualification.status" />
            <span class="ms-1 fw-bold text-success" style="font-size: 0.9rem;">YES</span>
          </label>
          <label class="radio-inline mb-0">
            <input type="radio" name="qualification_status" value="no" v-model="form.qualification.status" />
            <span class="ms-1 fw-bold text-danger" style="font-size: 0.9rem;">NO</span>
          </label>
          <label class="radio-inline mb-0">
            <input type="radio" name="qualification_status" value="na" v-model="form.qualification.status" />
            <span class="ms-1 fw-bold text-warning" style="font-size: 0.9rem;">N/A</span>
          </label>
        </div>
      </div>

      <div v-if="form.qualification.status === 'yes'" class="border rounded p-3 bg-light">
        <label class="form-label form-label-thin">Qualification Documents</label>
        <div v-for="(doc, docIndex) in form.qualification.documents" :key="docIndex"
          class="row align-items-center g-2 mb-2">
          <div class="col-md-5">
            <input type="text" class="form-control form-control-thin" v-model="doc.name"
              :name="`qualification_documents[${docIndex}][name]`" placeholder="Document name" />
          </div>
          <div class="col-md-5">
            <input type="file" class="form-control form-control-thin"
              @change="handleFileUpload(form.qualification, docIndex, $event)" />
          </div>
          <div class="col-md-2">
            <button v-if="docIndex > 0" type="button" class="btn btn-sm btn-outline-danger hover-white w-100"
              @click="removeQualificationDocument(docIndex)">
              <i class="fa fa-times"></i> Remove
            </button>
            <span v-else class="text-muted small">Required</span>
          </div>
        </div>
        <button type="button" class="btn btn-sm btn-outline-primary hover-white mt-1" @click="addQualificationDocument">
          <i class="fa fa-plus"></i> Add More
        </button>
      </div>
    </div>

    <!-- ==================== CHECKLIST SECTION (CONDITIONAL) ==================== -->
    <div v-if="form.vendor_type === 'contractor'" class="mb-4">
      <label class="form-label form-label-thin fw-bold">Contractor Compliance Checklist</label>
      <div class="border rounded p-3 bg-light">
        <div v-for="(item, index) in checklist" :key="item.id" class="mb-4 p-3 bg-white rounded border-start border-4"
          :class="{
            'border-success': item.status === 'yes',
            'border-danger': item.status === 'no',
            'border-warning': item.status === 'na',
            'border-secondary': !item.status
          }">

          <!-- Checklist Item Row -->
          <div class="d-flex align-items-center justify-content-between">
            <!-- Left: Sr No + Item Name -->
            <div class="flex-grow-1">
              <div class="fw-bold">
                <span class="badge bg-secondary me-2">{{ index + 1 }}</span>
                {{ item.item_name }}
              </div>
              <!-- <div class="text-muted small ms-4">{{ item.description }}</div> -->
            </div>

            <!-- Right: Radio Buttons -->
            <div class="flex-shrink-0 ms-3">
              <div class="d-flex gap-3">
                <label class="radio-inline mb-0">
                  <input type="radio" :name="'status_' + item.id" value="yes" v-model="item.status" required />
                  <span class="ms-1 fw-bold text-success" style="font-size: 0.9rem;">YES</span>
                </label>
                <label class="radio-inline mb-0">
                  <input type="radio" :name="'status_' + item.id" value="no" v-model="item.status" required />
                  <span class="ms-1 fw-bold text-danger" style="font-size: 0.9rem;">NO</span>
                </label>
                <label class="radio-inline mb-0">
                  <input type="radio" :name="'status_' + item.id" value="na" v-model="item.status" required />
                  <span class="ms-1 fw-bold text-warning" style="font-size: 0.9rem;">N/A</span>
                </label>

                <!-- Validation Error -->
                <span v-if="errors['checklist.' + index + '.status']" class="text-danger small">
                  {{ errors['checklist.' + index + '.status'][0] }}
                </span>
              </div>
            </div>
          </div>

          <!-- NA Reason -->
          <div v-if="item.status === 'na'" class="mt-3">
            <input type="text" class="form-control form-control-thin" v-model="item.comments"
              placeholder="Reason for N/A" required />
            <div v-if="errors['checklist.' + index + '.comments']" class="text-danger small mt-1">
              {{ errors['checklist.' + index + '.comments'][0] }}
            </div>
          </div>

          <!-- Documents Section (Show only when YES is selected) -->
          <div v-if="item.status === 'yes'" class="mt-3 pt-2 border-top">
            <!-- Document Rows -->
            <div v-for="(doc, docIndex) in item.documents" :key="docIndex" class="row align-items-center g-2 mb-2">

              <!-- File Input -->
              <div class="col-md-5">
                <input type="file" class="form-control form-control-thin"
                  @change="handleFileUpload(item, docIndex, $event)" />
              </div>

              <!-- Comment Input -->
              <div class="col-md-5">
                <input type="text" class="form-control form-control-thin" v-model="doc.comment"
                  placeholder="Document comment" />
              </div>

              <!-- Remove Button (Only for extra rows, not first) -->
              <div class="col-md-2">
                <button v-if="docIndex > 0" type="button" class="btn btn-sm btn-outline-danger hover-white w-100"
                  @click="removeDocument(item, docIndex)">
                  <i class="fa fa-times"></i> Remove
                </button>
                <span v-else class="text-muted small">Required</span>
              </div>
            </div>

            <!-- Add More Button -->
            <button type="button" class="btn btn-sm btn-outline-primary hover-white mt-1" @click="addDocumentRow(item)">
              <i class="fa fa-plus"></i> Add More
            </button>

            <!-- Validation Errors for Documents -->
            <div v-if="errors['checklist.' + index + '.documents']" class="text-danger small mt-1">
              {{ errors['checklist.' + index + '.documents'][0] }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-sm btn-outline-secondary hover-white" @click="close">
        <i class="fa fa-times"></i> Cancel
      </button>
      <button type="submit" class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn"
        :disabled="submitting">
        <i class="fa-solid fa-plus fs-12"></i> {{ submitting ? 'Creating...' : 'Create Vendor' }}
      </button>
    </div>
  </form>
</template>

<script>
import FileUploader from '@/components/FileUploader.vue';
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'
import FormModal from '@/components/utils/FormModal.vue'
import CreateAsset from '@/views/Asset/CreateAsset.vue'
import CreateLocation from '@/views/Location/CreateLocation.vue'
export default {
  name: 'CreateVendor',
  emits: ['created', 'close'],
  components: {
    MultiSelect,
    FileUploader,
    CreateAsset,
    FormModal,
    CreateLocation
  },

  data() {
    return {
      form: {
        name: '',
        description: '',
        files: [],
        vendor_type: '',
        contractor_nature: [],
        contractor_nature_other: '',
        contractor_risk_rating: '',
        vendor_nature: [],
        vendor_nature_other: '',
        vendor_risk_rating: '',
        qualification: {
          status: '',
          documents: [
            {
              name: '',
              file: null
            }
          ],
          uploaded_files: []
        },
        asset_ids: [],
        material_ids: [],
        location_ids: [],
        contacts: [
          {
            name: '',
            email: '',
            phone: '',
            role: ''
          }
        ]
      },
      checklist: [],
      errors: {},
      submitting: false,
      showRatingGuide: false,
      assets: [],
      materials: [],
      locations: [],
      dropdownCategories: [],
      checklistFetched: false, // Flag to prevent multiple API calls
      vendorTypeOptions: [
        { value: 'vendor', label: 'Vendor' },
        { value: 'contractor', label: 'Contractor' }
      ],
      contractorNatureOptions: [
        { value: 'electrical', label: 'Electrical' },
        { value: 'refrigeration', label: 'Refrigeration' },
        { value: 'mechanical', label: 'Mechanical' },
        { value: 'hvac', label: 'HVAC' },
        { value: 'cleaning', label: 'Cleaning' },
        { value: 'welding', label: 'Welding' },
        { value: 'landscaping', label: 'Landscaping' },
        { value: 'fabrication', label: 'Fabrication' },
        { value: 'equipment_installation', label: 'Equipment Installation' },
        { value: 'equipment_maintenance', label: 'Equipment Maintenance' },
        { value: 'pest_control', label: 'Pest Control' },
        { value: 'utility_maintenance', label: 'Utility Maintenance' },
        { value: 'forklift_maintenance', label: 'Forklift Maintenance' },
        { value: 'other', label: 'Other' }
      ],
      vendorNatureOptions: [
        { value: 'equipment', label: 'Equipment' },
        { value: 'parts', label: 'Parts' },
        { value: 'chemicals', label: 'Chemicals' },
        { value: 'supplies', label: 'Supplies' },
        { value: 'other', label: 'Other' }
      ],
      riskRatingOptions: [
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' }
      ],
      loading: false
    }
  },

  async mounted() {
    await Promise.all([
      this.fetchAssets(),
      this.fetchLocations(),
     this.fetchParentLocations()
    ])
    // Don't fetch checklist on mount - only when contractor selected
  },

  computed: {
    associatedLabel() {
      const singular = {
        chemicals: 'Chemicals',
        parts: 'Parts',
        supplies: 'Supplies',
        equipment: 'Equipment'
      }
      if (!this.form.vendor_nature || !this.form.vendor_nature.length) return 'Items'
      if (this.form.vendor_nature.length === 1) {
        return singular[this.form.vendor_nature[0]] || 'Items'
      }
      return 'Materials'
    },

    materialGroups() {
      if (!Array.isArray(this.form.vendor_nature) || !this.form.vendor_nature.length) return []
      const mapType = {
        chemicals: { type: 'chemical', label: 'Chemicals' },
        parts: { type: 'part', label: 'Parts' },
        supplies: { type: 'supplies', label: 'Supplies' },
        equipment: { type: 'equipment', label: 'Equipment' }
      }

      return this.form.vendor_nature
        .filter(n => mapType[n])
        .map(n => ({
          label: mapType[n].label,
          options: (this.materials || [])
            .filter(m => m.type === mapType[n].type)
            .map(m => ({ value: m.id, label: m.name || m.external_id || `#${m.id}` }))
        }))
        .filter(group => group.options.length)
    }
  },

  methods: {
    // ========== VENDOR TYPE CHANGE ==========
    async onVendorTypeChange(value) {
      if (value === 'contractor' && !this.checklistFetched) {
        await this.fetchChecklist()
        this.checklistFetched = true
      } else if (value !== 'contractor') {
        // Clear checklist when switching from contractor
        this.checklist = []
        this.checklistFetched = false
        this.form.contractor_nature = []
        this.form.contractor_nature_other = ''
        this.form.contractor_risk_rating = ''
      }

      if (value === 'vendor') {
        this.form.vendor_nature = Array.isArray(this.form.vendor_nature) ? this.form.vendor_nature : []
      } else {
        this.form.vendor_nature = []
        this.form.vendor_nature_other = ''
        this.form.vendor_risk_rating = ''
        this.form.qualification = {
          status: '',
          documents: [
            {
              name: '',
              file: null
            }
          ],
          uploaded_files: []
        }
      }
    },

    async onVendorNatureChange(value) {
      this.form.material_ids = []
      if (!Array.isArray(value) || !value.length) return
      const hasSelectableNature = value.some(nature => nature !== 'other')
      if (!hasSelectableNature) return
      await this.fetchMaterials()
    },

    // ========== CHECKLIST METHODS ==========
    async fetchChecklist() {
      try {
        const result = await form_api_submit({
          url: 'vendor/checklist',
          method: 'GET',
          show_success_toast: false,
          show_error_toast: true,
          remove_pre_toasts: false
        })

        if (result.success) {
          this.checklist = result.data.map(item => ({
            ...item,
            status: '', // No default selection
            comments: null,
            documents: [
              {
                file: null,
                comment: ''
              }
            ], // First row always exists
            uploaded_files: []
          }))
        }
      } catch (error) {
        console.error('Error fetching checklist:', error)
      }
    },

    handleFileUpload(item, docIndex, event) {
      const file = event.target.files[0]
      if (!file) return

      // Update the specific document row
      item.documents[docIndex].file = file

      // Track uploaded file for display
      if (!item.uploaded_files) item.uploaded_files = []

      // Check if already tracked
      const existing = item.uploaded_files.find(f => f.name === file.name)
      if (!existing) {
        item.uploaded_files.push({
          name: file.name,
          file: file
        })
      }
    },

    addDocumentRow(item) {
      item.documents.push({
        file: null,
        comment: ''
      })
    },

    addQualificationDocument() {
      this.form.qualification.documents.push({
        name: '',
        file: null
      })
    },

    removeQualificationDocument(index) {
      if (index > 0) {
        const removed = this.form.qualification.documents.splice(index, 1)[0]
        if (this.form.qualification.uploaded_files && removed.file) {
          const uploadedIndex = this.form.qualification.uploaded_files.findIndex(f => f.file === removed.file)
          if (uploadedIndex !== -1) {
            this.form.qualification.uploaded_files.splice(uploadedIndex, 1)
          }
        }
      }
    },

    removeDocument(item, index) {
      if (index > 0) { // Only allow removing extra rows
        const removed = item.documents.splice(index, 1)[0]

        // Remove from uploaded_files if exists
        if (item.uploaded_files && removed.file) {
          const fileIndex = item.uploaded_files.findIndex(f => f.file === removed.file)
          if (fileIndex !== -1) {
            item.uploaded_files.splice(fileIndex, 1)
          }
        }
      }
    },

    shouldShowDocuments(item) {
      return item.status === 'yes'
    },

    // ========== EXISTING METHODS ==========
    addContact() {
      this.form.contacts.push({
        name: '',
        email: '',
        phone: '',
        role: ''
      })
      this.$nextTick(() => {
        const contactCards = document.querySelectorAll('.contact-card');
        if (contactCards.length) {
          contactCards[contactCards.length - 1].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      });
    },

    removeContact(index) {
      this.form.contacts.splice(index, 1)
      Object.keys(this.errors).forEach(key => {
        if (key.startsWith(`contacts.${index}`)) {
          delete this.errors[key]
        }
      })
    },

    async handleSubmit() {
      this.submitting = true
      this.errors = {}

      try {
        const contacts = this.form.contacts.filter(c => c.name && c.name.trim())

        if (!contacts.length) {
          this.errors.contacts = 'At least one contact with name is required';
          return
        }

        const formData = new FormData()

        formData.append('name', this.form.name || '')
        formData.append('description', this.form.description || '')

        if (this.form.vendor_type !== null && this.form.vendor_type !== '') {
          formData.append('vendor_type', this.form.vendor_type)
        }

        const assetIds = Array.isArray(this.form.asset_ids) ? this.form.asset_ids : []
        const locationIds = Array.isArray(this.form.location_ids) ? this.form.location_ids : []

        assetIds.forEach(a => {
          if (a !== null && a !== '') formData.append('asset_ids[]', a)
        })

        const materialIds = Array.isArray(this.form.material_ids) ? this.form.material_ids : []
        materialIds.forEach(m => {
          if (m !== null && m !== '') formData.append('material_ids[]', m)
        })

        locationIds.forEach(l => {
          if (l !== null && l !== '') formData.append('location_ids[]', l)
        })

        if (Array.isArray(this.form.contractor_nature) && this.form.contractor_nature.length) {
          this.form.contractor_nature.forEach(value => {
            if (value !== null && value !== '') {
              formData.append('contractor_nature[]', value)
            }
          })
        } else if (this.form.contractor_nature && this.form.contractor_nature !== '') {
          formData.append('contractor_nature[]', this.form.contractor_nature)
        }

        if (this.form.contractor_nature_other && this.form.contractor_nature_other.trim()) {
          formData.append('contractor_nature_other', this.form.contractor_nature_other.trim())
        }

        if (this.form.contractor_risk_rating && this.form.contractor_risk_rating !== '') {
          formData.append('contractor_risk_rating', this.form.contractor_risk_rating)
        }

        if (Array.isArray(this.form.vendor_nature) && this.form.vendor_nature.length) {
          this.form.vendor_nature.forEach(v => {
            if (v !== null && v !== '') formData.append('vendor_nature[]', v)
          })
        }

        if (Array.isArray(this.form.vendor_nature) && this.form.vendor_nature.includes('other') && this.form.vendor_nature_other && this.form.vendor_nature_other.trim()) {
          formData.append('vendor_nature_other', this.form.vendor_nature_other.trim())
        }

        if (this.form.vendor_risk_rating && this.form.vendor_risk_rating !== '') {
          formData.append('vendor_risk_rating', this.form.vendor_risk_rating)
        }

        if (this.form.qualification && this.form.qualification.status) {
          formData.append('qualification_status', this.form.qualification.status)

          if (this.form.qualification.status === 'yes') {
            this.form.qualification.documents.forEach((doc, index) => {
              if (doc.name && doc.name.trim()) {
                formData.append(`qualification_documents[${index}][name]`, doc.name.trim())
              }
              if (doc.file) {
                formData.append(`qualification_documents[${index}][file]`, doc.file)
              }
            })
          }
        }

        contacts.forEach((contact, index) => {
          formData.append(`contacts[${index}][name]`, contact.name)
          formData.append(`contacts[${index}][email]`, contact.email || '')
          formData.append(`contacts[${index}][phone]`, contact.phone || '')
          formData.append(`contacts[${index}][role]`, contact.role || '')
        })

        const files = Array.isArray(this.form.files) ? this.form.files : []
        files.forEach(file => {
          formData.append('files[]', file)
        })

        // ===== CHECKLIST VALIDATION (Only if contractor) =====
        if (this.form.vendor_type === 'contractor') {
          let checklistErrors = {}

          this.checklist.forEach((item, index) => {
            // Check if status is selected
            if (!item.status) {
              if (!checklistErrors[`checklist.${index}.status`]) {
                checklistErrors[`checklist.${index}.status`] = []
              }
              checklistErrors[`checklist.${index}.status`].push('Please select YES, NO, or N/A')
            }

            // If status is YES, check for documents
            if (item.status === 'yes') {
              const hasFile = item.documents.some(doc => doc.file instanceof File || (doc.file && doc.file.name))

              if (!hasFile) {
                if (!checklistErrors[`checklist.${index}.documents`]) {
                  checklistErrors[`checklist.${index}.documents`] = []
                }
                checklistErrors[`checklist.${index}.documents`].push('Please upload at least one document')
              }
            }

            // If status is NA, check for reason
            if (item.status === 'na') {
              if (!item.comments || !item.comments.trim()) {
                if (!checklistErrors[`checklist.${index}.comments`]) {
                  checklistErrors[`checklist.${index}.comments`] = []
                }
                checklistErrors[`checklist.${index}.comments`].push('Please provide reason for N/A')
              }
            }
          })

          // Merge errors
          if (Object.keys(checklistErrors).length) {
            this.errors = { ...this.errors, ...checklistErrors }
            this.submitting = false
            return
          }
        }

        // ===== CHECKLIST PAYLOAD (Only if contractor) =====
        if (this.form.vendor_type === 'contractor' && this.checklist.length) {
          this.checklist.forEach((item, index) => {
            formData.append(`checklist[${index}][item_id]`, item.id ?? '')
            formData.append(`checklist[${index}][status]`, item.status || '')
            if (item.comments) {
              formData.append(`checklist[${index}][comments]`, item.comments)
            }

            if (item.documents && item.documents.length) {
              item.documents.forEach((doc, docIndex) => {
                if (doc.comment) {
                  formData.append(`checklist[${index}][documents][${docIndex}][comment]`, doc.comment)
                }
                if (doc.file instanceof File) {
                  formData.append(`checklist[${index}][documents][${docIndex}][file]`, doc.file)
                }
              })
            }
          })
        }

        const result = await form_api_submit({
          url: 'vendor/create',
          method: 'POST',
          data: formData,
          curr_form_id: '#vendor_create_form',
          take_form_data: false
        })

        if (result.success) {
          this.$emit('created', result.data?.vendor)
          this.close()
          this.resetForm()
        } else if (result.errors) {
          this.errors = result.errors
        }

      } finally {
        this.submitting = false
      }
    },

    close() {
      this.$emit('close')
    },

    resetForm() {
      this.form = {
        name: '',
        description: '',
        files: [],
        vendor_type: '',
        contractor_nature: [],
        contractor_nature_other: '',
        contractor_risk_rating: '',
        vendor_nature: [],
        vendor_nature_other: '',
        vendor_risk_rating: '',
        qualification: {
          status: '',
          documents: [
            {
              name: '',
              file: null
            }
          ],
          uploaded_files: []
        },
        asset_ids: [],
        material_ids: [],
        location_ids: [],
        contacts: [
          {
            name: '',
            email: '',
            phone: '',
            role: ''
          }
        ],
       Parentlocations: [],
      }
      this.checklist = []
      this.checklistFetched = false
      this.errors = {}
    },

    async fetchAssets() {
      this.loading = true
      const result = await form_api_submit({
        url: 'asset/dropdown',
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        this.assets = result.data.list || result.data || []
        this.dropdownCategories = result.data.list || result.data || []
      }
      this.loading = false
    },
    openCreateAssetModal() {
      this.$refs.createAssetModal.showModal()
    },

    closeCreateAssetModal() {
      this.$refs.createAssetModal.closeModal()
    },

    async onAssetCreated(assetData) {
      await this.fetchAssets()

      this.closeCreateAssetModal()

      if (
        assetData &&
        assetData.asset &&
        assetData.asset.id
      ) {
        const newAsset = this.assets.find(
          a => a.id === assetData.asset.id
        )

        if (newAsset) {
          if (!Array.isArray(this.form.asset_ids)) {
            this.form.asset_ids = []
          }

          this.form.asset_ids.push(newAsset.id)
        }
      }
    },
    async fetchLocations() {
      this.loading = true
      const result = await form_api_submit({
        url: 'locations/get-locations',
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        console.log('Fetched locations:', result.data)
        this.locations = result.data || []
      }
      this.loading = false
    },
    
    async fetchParentLocations() {
      this.loading = true

      const result = await form_api_submit({
        url: 'locations/get-locations',
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        this.Parentlocations = result.data
      }

      this.loading = false
    },
    openCreateLocationModal() {
  this.$refs.createLocationModal.showModal()
},

closeCreateLocationModal() {
  this.$refs.createLocationModal.closeModal()
},

async onLocationCreated(locationData) {
  await this.fetchLocations() // or get_locations(), whichever loads your locations

  this.closeCreateLocationModal()

  if (
    locationData &&
    locationData.location &&
    locationData.location.id
  ) {
    const newLocation = this.locations.find(
      l => l.id === locationData.location.id
    )

    if (newLocation) {
      if (!Array.isArray(this.form.location_ids)) {
        this.form.location_ids = []
      }

      this.form.location_ids.push(newLocation.id)
    }
  }
},
    async fetchMaterials() {
      this.loading = true
      const result = await form_api_submit({
        url: 'materials/dropdown',
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        this.materials = result.data.list || result.data || []
      }
      this.loading = false
    }
  }
}
</script>



<style scoped>
.contact-card {

  border-radius: 0.375rem;
  transition: all 0.2s ease-in-out;
}

/* .contact-card-highlight {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.1rem rgba(13, 110, 253, 0.25);
} */

.row {
  display: flex;
  flex-wrap: wrap;
  margin: -0.5rem;
}

.col-md-6 {
  flex: 0 0 50%;
  padding: 0.5rem;
}

@media (max-width: 768px) {
  .col-md-6 {
    flex: 0 0 100%;
  }
}

/* Checklist Styles */
.border-start {
  border-left-width: 4px !important;
}

.border-success {
  border-left-color: #28a745 !important;
}

.border-danger {
  border-left-color: #dc3545 !important;
}

.border-warning {
  border-left-color: #ffc107 !important;
}

.border-secondary {
  border-left-color: #6c757d !important;
}

/* Radio button styling */
.radio-inline {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0;
}

.radio-inline input[type="radio"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
  margin: 0;
}

.radio-inline span {
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1;
}



/* Badge styling */
.badge.bg-secondary {
  font-size: 0.8rem;
  padding: 0.35rem 0.65rem;
}

.rating-guide-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 1.5rem;
}

.rating-guide-modal {
  width: min(560px, 100%);
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0.75rem 1.5rem rgba(0, 0, 0, 0.18);
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.25rem;
}

.rating-guide-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.rating-guide-body p {
  margin-bottom: 0.75rem;
}

.rating-guide-table {
  width: 100%;
  border-collapse: collapse;
}

.rating-guide-table th,
.rating-guide-table td {
  border: 1px solid #dee2e6;
  padding: 0.75rem;
  vertical-align: top;
}

.rating-guide-table th {
  background: #f8f9fa;
  color: #212529;
}

.risk-level-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
}

.risk-low {
  background: #198754;
}

.risk-medium {
  background: #ffc107;
  color: #212529;
}

.risk-high {
  background: #dc3545;
}

.risk-critical {
  background: #6f42c1;
}

.rating-guide-footer {
  margin-top: 1rem;
}
</style>